import React from 'react';

import { reverse } from 'named-urls';

import { useGridApiRef } from '@mui/x-data-grid-pro';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';

import { Button } from 'components/Button';

import routes from 'core/routes';
import { useNavBarHeight } from 'utils/hooks/navbar';

import API from 'utils/api/api';

import Logger from 'utils/logger';

import { EntityModules } from 'core/constants';

import { EntityFormType } from 'utils/api/modules/entities/entities.types';

import { useStore } from 'utils/hooks/store';

import { GridState } from 'components/Grid/types';

import { UuidableName } from 'utils/api/types';

import EventsTable from './components/EventsTable';
import CreateEventModal from './components/CreateEventModal';
import PipelinePicker from './components/PipelinesPicker';

const PIPELINE_FILTER_FIELD = 'events__pipeline';

const Events: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const refreshRef = React.useRef<Function | null>(null);
  const { routing } = useStore();
  const [openModal, setOpenModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [forms, setForms] = React.useState<EntityFormType[]>([]);
  const [drafts, setDrafts] = React.useState<EntityFormType[]>([]);

  const result = localStorage.getItem(`grid_state_/api/entities/events/`);

  const initialState = React.useMemo(() => (result ? (JSON.parse(result) as GridState) : null), [result]);

  const [pipelineFilter, setPipelineFilter] = React.useState<string | null>(
    result ? initialState?.filters?.find((a) => a.field === PIPELINE_FILTER_FIELD)?.value || null : null
  );

  const onClose = (created: boolean): void => {
    setOpenModal(false);
    if (refreshRef.current && created) {
      refreshRef.current();
    }
  };

  const onRefreshCallback = (callback: Function): void => {
    refreshRef.current = callback;
  };

  React.useEffect(() => {
    const fetchFormsAndDrafts = async (): Promise<void> => {
      setLoading(true);
      try {
        const { data: formsData } = await API.entities.getEntityForms(EntityModules.Events, {
          audience: '-public',
          active: '1',
        })();

        const { data: draftsData } = await API.entities.getEntityFormsDrafts(EntityModules.Events)();

        setForms(formsData.results);
        setDrafts(draftsData.results);
      } catch (error) {
        Logger.error(error as string);
      } finally {
        setLoading(false);
      }
    };

    fetchFormsAndDrafts();
  }, []);

  const handleNewButtonClick = async (): Promise<void> => {
    // if there is only one form, and no drafts create a new draft
    if (forms.length === 1 && drafts.length === 0) {
      try {
        const { data } = await API.entities.createEntityFormsDrafts(EntityModules.Events, forms[0].uuid)();
        routing.push(reverse(routes.dashboard.events.draft.details, { draftId: data.uuid }));
      } catch (error) {
        Logger.error(error as string);
      }
    }
    setOpenModal(true);
  };

  const gridApiRef = useGridApiRef();

  const handlePipelineChange = (value: UuidableName | null): void => {
    setPipelineFilter(value?.uuid || null);

    if (!gridApiRef.current) return;

    // get all filters without events__pipelines
    const filterItems = gridApiRef.current.state.filter.filterModel.items.filter(
      (a) => a.field !== PIPELINE_FILTER_FIELD
    );

    const pipelineFilterItem =
      value && value.uuid !== '' && value.uuid !== null
        ? {
            field: PIPELINE_FILTER_FIELD,
            operator: '$>',
            value: value.uuid,
          }
        : null;

    gridApiRef.current.setFilterModel({
      ...gridApiRef.current.state.filter.filterModel,
      items: pipelineFilterItem ? [...filterItems, pipelineFilterItem] : filterItems,
    });
    if (refreshRef.current) {
      refreshRef.current();
    }
  };

  return (
    <>
      <Record
        Header={
          <MainHeader
            title="Events"
            breadcrumbs={[
              { label: 'Events', link: routes.dashboard.events.toString() },
              { label: 'Overview', link: routes.dashboard.events.toString() },
            ]}
            action={
              <>
                <PipelinePicker value={pipelineFilter} onChange={handlePipelineChange} initialShouldLoad />

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleNewButtonClick}
                  disabled={forms.length === 0 || loading}
                >
                  Report Event
                </Button>
              </>
            }
          />
        }
        Content={(onError) => (
          <EventsTable onError={onError} onRefreshCallback={onRefreshCallback} gridApiRef={gridApiRef} />
        )}
        navBarHeight={navBarHeight}
      />
      <CreateEventModal openModal={openModal} onClose={onClose} forms={forms} drafts={drafts} />
    </>
  );
};

export default Events;
